import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSession } from "../contexts/SessionContext";
import { CsrAPI } from "../apis/CsrAPI";
import { setSentryUser } from "../utils/Sentry";

export const LoginCallbackHandler = ({ children }) => {
  const { setSessionReady, setLoginForbidden } = useSession();
  const { isAuthenticated, user } = useAuth0();
  // Which subject we have already run the backend login for. `undefined`
  // means "nobody yet"; null is a real value (authenticated but no `sub`).
  const loggedInSubRef = React.useRef();

  React.useEffect(() => {
    if (!isAuthenticated) {
      // Allow a fresh login (possibly as a different user) to run again.
      loggedInSubRef.current = undefined;
      return;
    }

    // Auth0 hands back a brand-new `user` object every time it mints a new
    // ID token, so this effect re-runs on every silent token renewal — which
    // happens on the first request after the tab has been idle. Without this
    // guard each renewal drove sessionReady back to false, remounting the
    // routed tree mid-session for no reason. Key off the subject claim so the
    // backend login runs once per signed-in user instead.
    const sub = user?.sub ?? null;
    if (loggedInSubRef.current === sub) {
      return;
    }
    loggedInSubRef.current = sub;

    const handleLogin = async () => {
      setSessionReady(false);
      setSentryUser(user);
      try {
        await CsrAPI.login();
      } catch (error) {
        // A 403 means the backend refuses this user, so LoginForbiddenGate
        // moves them to the access-denied page. Any other failure is already
        // logged by the axios interceptor, and leaving the session unready
        // keeps the app on its loader rather than on half-loaded pages.
        if (error?.response?.status === 403) {
          setLoginForbidden(true);
        }
        return;
      }
      setLoginForbidden(false);
      setSessionReady(true);
    };

    handleLogin();
  }, [isAuthenticated, setSessionReady, setLoginForbidden, user]);

  return children;
};
